import type { VoyageDetails } from "../../shared/api/types";
import type { VoyageStopOverride } from "../../shared/api/endpoints";
import { resolveOverrides } from "../voyage-details/override-identity";

export interface OrphanVoyage { vesselCode: string; voyageNo: string; voyageReference: string; count: number; }

// Voyages currently loaded whose overrides fail to resolve against the
// itinerary (see override-identity.ts::resolveOverrides). Dedupes by
// (vesselCode, voyageNo) and skips voyages without cached details yet —
// callers re-run this as details/overrides arrive (overridesLoadedCount /
// cachedDetailsCount) so it converges once both caches are warm.
export function orphanVoyages(
  voyages: readonly { vesselCode: string; voyageNo: string }[],
  getDetails: (vc: string, vn: string) => VoyageDetails | null,
  getOverrides: (vc: string, vn: string) => readonly VoyageStopOverride[],
): OrphanVoyage[] {
  const seen = new Set<string>();
  const out: OrphanVoyage[] = [];
  for (const v of voyages) {
    const key = `${v.vesselCode}/${v.voyageNo}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const d = getDetails(v.vesselCode, v.voyageNo);
    if (!d) continue;
    const { orphans } = resolveOverrides(getOverrides(v.vesselCode, v.voyageNo), d.itineraries ?? []);
    if (orphans.length > 0) out.push({ vesselCode: v.vesselCode, voyageNo: v.voyageNo, voyageReference: d.voyageReference, count: orphans.length });
  }
  return out;
}

// Sum of orphaned overrides across a cluster's voyages. Dedupes by
// (vesselCode, voyageNo) — a cluster can list the same voyage twice
// (e.g. it calls the terminal more than once), and resolveOverrides
// already returns the WHOLE-voyage orphan count, so counting it once per
// voyage id avoids double-counting the same voyage's orphans. Skips
// voyages whose details aren't cached yet: without the itinerary an
// override can't be judged an orphan, and counting it as one would flash
// the marker on every override-bearing bar during load (overrides warm
// in bulk before details land). Shared by the tooltip warning and the
// calendar stripe marker so the two never disagree.
export function countClusterOrphans(
  voyages: readonly { vesselCode: string; voyageNo: string }[],
  getDetails: (vc: string, vn: string) => VoyageDetails | null,
  getOverrides: (vc: string, vn: string) => readonly VoyageStopOverride[],
): number {
  const seen = new Set<string>();
  let total = 0;
  for (const v of voyages) {
    const key = `${v.vesselCode}/${v.voyageNo}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const d = getDetails(v.vesselCode, v.voyageNo);
    if (!d) continue;
    total += resolveOverrides(getOverrides(v.vesselCode, v.voyageNo), d.itineraries ?? []).orphans.length;
  }
  return total;
}
